import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import Button from '../shared/Button.jsx';
import Logo from './Logo.jsx';
import { navLinks } from '../constants/navigation.js';

/* The pill: one white capsule holding all eight pages, the current one filled
 * green. Below xl there is no honest way to fit eight labels beside the logo
 * and a call to action, so it collapses into the sheet. */
const PILL_BASE =
  'rounded-full px-[15px] py-[9px] text-sm whitespace-nowrap transition-colors';
const PILL_ON = 'bg-forest font-extrabold text-cream';
const PILL_OFF = 'font-bold text-ink-mute hover:bg-mint hover:text-forest';

export default function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);

  // Tapping a link has to close the sheet as well as navigate, or the new page
  // arrives behind an overlay that is still covering it.
  const close = () => setMenuOpen(false);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-60 border-b border-forest/10 bg-cream/85 backdrop-blur-[14px]">
      <div className="mx-auto flex max-w-[1240px] items-center gap-4 px-5 py-3.5 sm:px-8">
        <Logo />

        <nav
          aria-label="Main"
          className="ml-auto hidden min-w-0 max-w-full items-center gap-0.5 overflow-x-auto rounded-full border-[1.5px] border-line bg-white p-[5px] shadow-[0_2px_5px_rgb(20_83_45_/_0.06)] xl:flex"
        >
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              // `end` so Home is only current on the homepage itself.
              end={link.to === '/'}
              className={({ isActive }) => `${PILL_BASE} ${isActive ? PILL_ON : PILL_OFF}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden flex-none items-center xl:flex">
          <Button as={Link} to="/#programs" variant="coral" size="sm">
            Explore programs
          </Button>
        </div>

        <button
          type="button"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
          className="ml-auto flex size-11 flex-none flex-col items-center justify-center gap-[5px] rounded-full border-2 border-forest text-forest xl:hidden"
        >
          <span
            className={`block h-0.5 w-5 bg-current transition-transform ${
              menuOpen ? 'translate-y-[7px] rotate-45' : ''
            }`}
          />
          <span
            className={`block h-0.5 w-5 bg-current transition-opacity ${menuOpen ? 'opacity-0' : ''}`}
          />
          <span
            className={`block h-0.5 w-5 bg-current transition-transform ${
              menuOpen ? '-translate-y-[7px] -rotate-45' : ''
            }`}
          />
        </button>
      </div>

      {menuOpen && (
        <div
          id="mobile-nav"
          className="border-t border-forest/10 bg-cream px-5 pb-6 pt-2 sm:px-8 xl:hidden"
        >
          <nav aria-label="Main" className="flex flex-col">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={close}
                className={({ isActive }) =>
                  `border-b border-line py-3.5 font-display text-xl font-bold ${
                    isActive ? 'text-coral' : 'text-forest'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-5">
            <Button as={Link} to="/#programs" variant="coral" size="md" block onClick={close}>
              Explore programs
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
